import { useEffect, useState } from 'react';

/** Hodnota barvy v zrcadle = „#rrggbb" (malá písmena). */
const HEX = /^#[0-9a-f]{6}$/;

function normalizeHex(v: string): string | null {
  const t = v.trim().toLowerCase();
  const withHash = t.startsWith('#') ? t : `#${t}`;
  // Zkrácený zápis #abc → #aabbcc.
  if (/^#[0-9a-f]{3}$/.test(withHash)) {
    const [r, g, b] = withHash.slice(1);
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return HEX.test(withHash) ? withHash : null;
}

/**
 * Pole typu „color" ve formuláři zdrojů (náhled barvy zipu): nativní výběr
 * barvy + textové pole pro přesný kód (#1f2937) a tlačítko na vymazání.
 */
export function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string | null) => void;
}) {
  const current = normalizeHex(value);
  const [text, setText] = useState(current ?? '');

  // Hodnota přišla zvenku (načtení záznamu, výběr v pickeru) — srovnej text.
  useEffect(() => {
    setText(current ?? '');
  }, [current]);

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={current ?? '#000000'}
        onChange={(e) => onChange(e.target.value.toLowerCase())}
        aria-label="Vybrat barvu"
        className="h-[38px] w-14 shrink-0 cursor-pointer rounded-[5px] border border-[#cbd5e1] bg-white p-1"
      />
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const hex = normalizeHex(e.target.value);
          if (hex) onChange(hex);
        }}
        onBlur={() => setText(current ?? '')}
        placeholder="#000000"
        className="w-full border border-[#cbd5e1] rounded-[5px] px-3 py-2 text-sm font-semibold text-text bg-white placeholder:text-text-muted placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      />
      {current && (
        <button
          type="button"
          onClick={() => onChange(null)}
          title="Vymazat barvu"
          className="text-text-muted hover:text-danger px-1 cursor-pointer shrink-0"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/** Barevné kolečko do buňky tabulky (prázdná hodnota = nic). */
export function ColorSwatch({ value }: { value: string }) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  return (
    <span
      // Barva je data záznamu — Tailwind třídu pro libovolný hex nemá.
      style={{ backgroundColor: hex }}
      title={hex}
      className="inline-block h-6 w-6 rounded-full border border-border align-middle"
    />
  );
}
